import React from 'react'
import { Section, SectionTitle, SubSection, SubSectionTitle, Text, List, Table, CodeBlock } from '../styles'

export default function BlockchainSection() {
  return (
    <Section id="blockchain">
      <SectionTitle>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1.7 12.5l-1.4 1.4L12 13l-3.9 3.9-1.4-1.4L12 9.9l5.3 5.6z"/>
        </svg>
        4. Blockchain Integration
      </SectionTitle>
      
      <SubSection id="solana">
        <SubSectionTitle>4.1 Solana Integration</SubSectionTitle>
        <Text>
          The platform leverages Solana's high-performance blockchain for
          secure authentication, transaction processing, and project state
          management:
        </Text>
        <CodeBlock>
{`// Solana Program Interface
#[program]
pub mod ai_agent_platform {
    use super::*;

    pub fn initialize_project(
        ctx: Context<InitializeProject>,
        project_data: ProjectData
    ) -> Result<()> {
        // Project initialization logic
        let project = &mut ctx.accounts.project;
        project.owner = ctx.accounts.owner.key();
        project.data = project_data;
        project.status = ProjectStatus::Active;
        project.created_at = Clock::get()?.unix_timestamp;
        
        // Initialize agent accounts
        for agent_type in project_data.agent_types.iter() {
            let agent_account = &mut ctx.accounts.agent_accounts[agent_type];
            agent_account.initialize(
                project.key(),
                agent_type.clone(),
                project_data.agent_configs[agent_type].clone()
            )?;
        }
        
        Ok(())
    }

    pub fn update_project_state(
        ctx: Context<UpdateProject>,
        update_data: ProjectUpdateData
    ) -> Result<()> {
        // State update logic
        let project = &mut ctx.accounts.project;
        project.apply_update(update_data)?;
        
        // Emit state update event
        emit!(ProjectStateUpdated {
            project: project.key(),
            timestamp: Clock::get()?.unix_timestamp,
            update_type: update_data.update_type,
            metrics: update_data.metrics
        });
        
        Ok(())
    }

    pub fn process_agent_action(
        ctx: Context<AgentAction>,
        action_data: AgentActionData
    ) -> Result<()> {
        // Validate agent permissions
        require!(
            ctx.accounts.agent.has_permission(action_data.action_type),
            AgentError::InsufficientPermissions
        );
        
        // Execute agent action
        ctx.accounts.agent.execute_action(action_data)?;
        
        // Update project state
        let project = &mut ctx.accounts.project;
        project.update_agent_state(
            ctx.accounts.agent.key(),
            action_data.result
        )?;
        
        Ok(())
    }
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="smart-contracts">
        <SubSectionTitle>4.2 Smart Contract Architecture</SubSectionTitle>
        <Text>
          Our smart contract architecture is designed for efficient on-chain
          project management and agent coordination:
        </Text>
        <List>
          <li>
            Project Management Contracts:
            <ul>
              <li>Project initialization and configuration</li>
              <li>Milestone tracking and verification</li>
              <li>Resource allocation and management</li>
              <li>Payment distribution</li>
              <li>Project state management</li>
            </ul>
          </li>
          <li>
            Agent Coordination Contracts:
            <ul>
              <li>Agent registration and verification</li>
              <li>Task assignment and tracking</li>
              <li>Performance metrics and rewards</li>
              <li>Dispute resolution</li>
              <li>Inter-agent communication</li>
            </ul>
          </li>
          <li>
            Governance Contracts:
            <ul>
              <li>Platform parameters management</li>
              <li>Upgrade proposals and voting</li>
              <li>Fee structure management</li>
              <li>Emergency controls</li>
              <li>Protocol upgrades</li>
            </ul>
          </li>
        </List>
      </SubSection>

      <SubSection id="transactions">
        <SubSectionTitle>4.3 Transaction Processing</SubSectionTitle>
        <Text>
          The platform implements efficient transaction processing with
          optimized data structures and validation:
        </Text>
        <CodeBlock>
{`// Transaction Structure
{
  "transaction_type": "project_update",
  "version": "1.0",
  "timestamp": "2024-01-20T10:30:00Z",
  "project_id": "proj_123",
  "sender": {
    "address": "8xk7nAstQYh5mzqKjYbzgChU6RmNYJA4PWuvKSzYE2e7",
    "type": "agent",
    "signature": "base64_signature_data",
    "nonce": 12345
  },
  "payload": {
    "update_type": "milestone_completion",
    "milestone_id": "milestone_456",
    "deliverables": [
      {
        "type": "code_repository",
        "url": "https://github.com/example/repo",
        "commit_hash": "abc123",
        "verification_hash": "def456"
      },
      {
        "type": "deployment",
        "url": "https://example.com",
        "version": "1.0.0",
        "deployment_id": "deploy_789"
      }
    ],
    "metrics": {
      "code_quality": 95,
      "test_coverage": 98,
      "performance_score": 92,
      "security_rating": 96
    }
  },
  "fee": {
    "amount": 0.00001,
    "token": "SOL",
    "priority": "high"
  }
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="tokenomics">
        <SubSectionTitle>4.4 Tokenomics & Incentives</SubSectionTitle>
        <Text>
          The platform implements a sophisticated tokenomics model to
          incentivize quality contributions and efficient collaboration:
        </Text>
        <Table>
          <thead>
            <tr>
              <th>Component</th>
              <th>Mechanism</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Agent Rewards</td>
              <td>Performance-based token distribution</td>
              <td>Incentivize high-quality work</td>
            </tr>
            <tr>
              <td>Staking</td>
              <td>Token locking for agent activation</td>
              <td>Ensure commitment and quality</td>
            </tr>
            <tr>
              <td>Governance</td>
              <td>Token-weighted voting</td>
              <td>Platform parameter control</td>
            </tr>
            <tr>
              <td>Fee Distribution</td>
              <td>Automated revenue sharing</td>
              <td>Sustainable ecosystem</td>
            </tr>
            <tr>
              <td>Quality Bonuses</td>
              <td>Extra rewards for exceptional work</td>
              <td>Promote excellence</td>
            </tr>
            <tr>
              <td>Collaboration Rewards</td>
              <td>Shared rewards for team success</td>
              <td>Encourage cooperation</td>
            </tr>
          </tbody>
        </Table>
      </SubSection>
    </Section>
  )
}
